import React, { useEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

/**
 * The rendered half of useConfirm(). Not used directly — see ConfirmContext.
 *
 * Focus is placed on Cancel rather than Confirm: these dialogs guard
 * destructive work, so the safe option should be the one a stray Enter hits.
 * When the dialog collects a reason, focus starts on the picker instead — the
 * confirm button is disabled until a reason is chosen, so Enter still can't
 * complete the action by accident.
 */
const ConfirmDialog = ({
    title, message, confirmLabel, cancelLabel, tone,
    reasons, reasonLabel, notePlaceholder, noteRequiredFor,
    requireDate, dateLabel, minDate,
    onConfirm, onCancel,
}) => {
    const dialogRef = useRef(null);
    const cancelRef = useRef(null);
    const reasonRef = useRef(null);
    const dateRef = useRef(null);
    const previouslyFocused = useRef(null);

    const [reason, setReason] = useState('');
    const [note, setNote] = useState('');
    const [date, setDate] = useState('');

    const needsNote = !!noteRequiredFor && reason === noteRequiredFor;
    const canConfirm = (!reasons || (!!reason && (!needsNote || note.trim().length > 0)))
        && (!requireDate || !!date);

    useEffect(() => {
        previouslyFocused.current = document.activeElement;
        if (reasons) reasonRef.current?.focus();
        else if (requireDate) dateRef.current?.focus();
        else cancelRef.current?.focus();

        // Restore focus to whatever opened the dialog, so keyboard users aren't
        // dumped back at the top of the document.
        return () => {
            const el = previouslyFocused.current;
            if (el && typeof el.focus === 'function') el.focus();
        };
    }, [reasons, requireDate]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
                return;
            }
            if (e.key !== 'Tab') return;

            // Keep Tab inside the dialog. Includes the reason controls, which are
            // otherwise skipped and leave the user tabbing between the two buttons.
            // :not([disabled]) matters — the confirm button starts disabled while a
            // reason is outstanding, and a disabled button can't hold focus, so leaving
            // it in the list would let Tab escape the dialog.
            const focusable = dialogRef.current?.querySelectorAll(
                'button:not([disabled]), select, textarea, input'
            );
            if (!focusable || focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onCancel]);

    return (
        <div className="confirm-overlay" onMouseDown={onCancel}>
            <div
                ref={dialogRef}
                className={`confirm-dialog${tone === 'danger' ? ' confirm-dialog--danger' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirmDialogTitle"
                aria-describedby={message ? 'confirmDialogMessage' : undefined}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <h2 className="confirm-dialog__title" id="confirmDialogTitle">{title}</h2>
                {message && (
                    <p className="confirm-dialog__message" id="confirmDialogMessage">{message}</p>
                )}
                {reasons && (
                    <div className="confirm-dialog__reason">
                        <label className="confirm-dialog__label" htmlFor="confirmDialogReason">
                            {reasonLabel || 'Reason'}
                        </label>
                        <select
                            id="confirmDialogReason"
                            ref={reasonRef}
                            className="confirm-dialog__select"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            required
                        >
                            <option value="">Select a reason…</option>
                            {/* An entry carrying `options` is a group; a flat
                                [{value,label}] list still works unchanged. */}
                            {reasons.map(r => (r.options ? (
                                <optgroup key={r.label} label={r.label}>
                                    {r.options.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </optgroup>
                            ) : (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            )))}
                        </select>
                        <textarea
                            className="confirm-dialog__note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            maxLength={500}
                            placeholder={needsNote
                                ? (notePlaceholder || 'Please describe what happened (required)')
                                : (notePlaceholder || 'Add any detail (optional)')}
                            aria-label={needsNote ? 'Reason detail (required)' : 'Reason detail (optional)'}
                        />
                    </div>
                )}
                {requireDate && (
                    <div className="confirm-dialog__reason">
                        <label className="confirm-dialog__label" htmlFor="confirmDialogDate">
                            {dateLabel || 'Date'}
                        </label>
                        <input
                            id="confirmDialogDate"
                            ref={dateRef}
                            type="date"
                            className="confirm-dialog__date"
                            value={date}
                            min={minDate || new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setDate(e.target.value)}
                            required
                        />
                    </div>
                )}
                <div className="confirm-dialog__actions">
                    <button
                        ref={cancelRef}
                        type="button"
                        className="confirm-dialog__btn confirm-dialog__btn--cancel"
                        onClick={onCancel}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className={`confirm-dialog__btn confirm-dialog__btn--confirm${tone === 'danger' ? ' confirm-dialog__btn--danger' : ''}`}
                        onClick={() => {
                            if (!reasons && !requireDate) { onConfirm(undefined); return; }
                            onConfirm({
                                ...(reasons ? { reason, note: note.trim() } : {}),
                                ...(requireDate ? { date } : {}),
                            });
                        }}
                        disabled={!canConfirm}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;
